import * as React from 'react';
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import {
  ArrowsUpDownIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/solid';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SelectionActionBar } from '@/components/ui/selection-action-bar';
import { TableFooter } from '@/components/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { PageHeader } from '@/components/ui/page-header';
import type { ContactList } from '@/lib/services/contact-lists';
import { contactListService } from '@/lib/services/contact-lists';

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((res) => res.json());

export default function ContactListsPage() {
  const navigate = useNavigate();
  const { data: lists, isLoading, mutate } = useSWR<ContactList[]>('/api/contact-lists', fetcher);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedListIds, setSelectedListIds] = useState<number[]>([]);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const contactLists = Array.isArray(lists) ? lists : [];

  const handleCreateList = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newListName.trim()) return;
    setIsCreating(true);
    try {
      const newList = await contactListService.createList({ name: newListName.trim() });
      mutate();
      setNewListName('');
      setIsCreateOpen(false);
      toast.success('List created');
      navigate(`/dashboard/lists/${newList.id}`);
    } catch {
      toast.error('Failed to create list');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteList = async (listId: number) => {
    if (!confirm('Are you sure you want to delete this list? Contacts will not be deleted.')) return;
    try {
      await contactListService.deleteList(listId);
      mutate();
      toast.success('List deleted');
    } catch {
      toast.error('Failed to delete list');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedListIds.length === 0) return;
    setIsDeleting(true);
    try {
      await Promise.all(selectedListIds.map((id) => contactListService.deleteList(id)));
      setRowSelection({});
      setIsDeleteDialogOpen(false);
      setSelectedListIds([]);
      mutate();
      toast.success(`Deleted ${selectedListIds.length} list${selectedListIds.length > 1 ? 's' : ''}`);
    } catch {
      toast.error('Failed to delete some lists');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: ColumnDef<ContactList, any>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <div className="flex items-center h-full">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center h-full">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="!p-0 h-auto font-normal hover:text-foreground hover:bg-transparent"
        >
          Name
          <ArrowsUpDownIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="font-normal truncate max-w-[300px]">
          {row.getValue('name')}
        </div>
      ),
    },
    {
      accessorKey: 'memberCount',
      header: () => <div className="text-right">Contacts</div>,
      cell: ({ row }) => (
        <div className="text-right font-normal">{row.getValue('memberCount') as number}</div>
      ),
    },
    {
      accessorKey: 'campaignName',
      header: 'Campaign',
      cell: ({ row }) => {
        const list = row.original;
        if (!list.campaignName) return <span className="text-content-subtle text-sm">—</span>;
        return (
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-normal bg-surface-subtle text-foreground whitespace-nowrap cursor-pointer hover:bg-gray-200 transition-colors"
            onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/campaigns/${list.campaignId}/edit`); }}
          >
            {list.campaignName.length > 30 ? list.campaignName.slice(0, 30) + '...' : list.campaignName}
          </span>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: () => <div className="text-right">Created</div>,
      cell: ({ row }) => (
        <div className="text-right font-normal text-muted-foreground">
          {new Date(row.getValue('createdAt') as string).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </div>
      ),
    },
    {
      id: 'actions',
      enableHiding: false,
      header: () => <div className="text-right mr-4">Actions</div>,
      cell: ({ row }) => {
        const list = row.original;
        return (
          <div className="flex justify-end mr-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" className="h-10 w-10 p-0 has-[>svg]:px-0 bg-card">
                  <span className="sr-only">Open menu</span>
                  <EllipsisHorizontalIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/dashboard/lists/${list.id}`)}>
                  View list
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-danger-foreground"
                  onClick={() => handleDeleteList(list.id)}
                >
                  Delete list
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: contactLists,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const handleOpenDeleteDialog = () => {
    const selectedRows = table.getFilteredSelectedRowModel().rows;
    const ids = selectedRows.map(row => row.original.id);
    setSelectedListIds(ids);
    setIsDeleteDialogOpen(true);
  };

  return (
    <>
      <PageHeader title="Lists" className="mt-page-top mb-header-gap">
        <Input
          placeholder="Search lists"
          value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
          onChange={(event) =>
            table.getColumn('name')?.setFilterValue(event.target.value)
          }
          className="max-w-sm w-70"
          prefixIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
        />
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button variant="primary">Create list</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader className="mt-2 mb-4">
              <DialogTitle>Create List</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateList} className="space-y-4">
              <Input
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="required"
                inlineLabel="Name"
                className="w-full mt-2"
                autoFocus
                required
              />
              <div className="flex gap-3 pt-4 justify-end -mr-2 -mb-2">
                <Button type="button" variant="secondary" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={isCreating || !newListName.trim()}>
                  {isCreating ? 'Creating...' : 'Create List'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="w-full">
        {isLoading ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead><Skeleton className="h-5 w-5 rounded-full ml-2" /></TableHead>
                <TableHead><Skeleton className="h-4 w-12" /></TableHead>
                <TableHead className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableHead>
                <TableHead className="text-right"><Skeleton className="h-4 w-20 ml-auto" /></TableHead>
                <TableHead className="text-right"><Skeleton className="h-4 w-20 ml-auto" /></TableHead>
                <TableHead><div className="flex justify-end mr-4"><Skeleton className="h-4 w-12" /></div></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-5 w-5 rounded-full ml-2" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                  <TableCell className="text-right"><Skeleton className="h-4 w-8 ml-auto" /></TableCell>
                  <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                  <TableCell className="text-right"><Skeleton className="h-4 w-20 ml-auto" /></TableCell>
                  <TableCell><div className="flex justify-end"><Skeleton className="h-8 w-8 mr-4" /></div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <>
            <div className="relative" ref={tableContainerRef} style={{ contain: 'layout paint' }}>
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                        className="cursor-pointer"
                        onClick={(e) => {
                          if (
                            e.target instanceof Element &&
                            (e.target.closest('button') ||
                             e.target.closest('input') ||
                             e.target.closest('[role="button"]') ||
                             e.target.closest('[data-radix-collection-item]'))
                          ) {
                            return;
                          }
                          navigate(`/dashboard/lists/${row.original.id}`);
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="text-center">
                        <div className="py-24">
                          <p className="text-sm text-content-strong">No lists yet.</p>
                          <Button variant="primary" className="mt-3" onClick={() => setIsCreateOpen(true)}>Create list</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <SelectionActionBar
              selectedCount={table.getFilteredSelectedRowModel().rows.length}
              isVisible={table.getFilteredSelectedRowModel().rows.length > 0}
              onClearSelection={() => setRowSelection({})}
              itemName={{ singular: 'list', plural: 'lists' }}
              tableContainerRef={tableContainerRef}
              selectedRowIds={table.getFilteredSelectedRowModel().rows.map(row => row.id)}
            >
              <Button
                variant="destructive"
                size="xs"
                onClick={handleOpenDeleteDialog}
              >
                Delete
              </Button>
            </SelectionActionBar>

            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Delete Lists</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-sm text-muted-foreground">
                    Are you sure you want to delete {selectedListIds.length} list{selectedListIds.length > 1 ? 's' : ''}? Contacts will not be deleted.
                  </p>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="secondary" onClick={() => setIsDeleteDialogOpen(false)} disabled={isDeleting}>
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={handleBulkDelete} disabled={isDeleting}>
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <TableFooter>
              <span className="text-sm text-foreground whitespace-nowrap">
                <span className="font-bold">{table.getFilteredRowModel().rows.length}</span> list{table.getFilteredRowModel().rows.length !== 1 ? 's' : ''}{table.getFilteredRowModel().rows.length > 0 && ` · showing ${table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}–${Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length)}`}
              </span>
              {table.getPageCount() > 1 && (
                <div className="space-x-2">
                  <Button variant="secondary" size="sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                    Previous
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                    Next
                  </Button>
                </div>
              )}
            </TableFooter>
          </>
        )}
      </div>
    </>
  );
}
